import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from '../api/axios';
import styles from './AfterActionReports.module.css';
import { Calendar, CheckCircle, Clock, FileText, Users } from 'lucide-react';

interface PendingEvent {
  eventId: number;
  title: string;
  startDate: string;
  endDate: string;
  hasAAR: boolean;
  aarId?: number;
  rsvpCount: number;
}

interface AfterActionReport {
  id: number;
  eventId: number;
  eventTitle: string;
  eventStartDate: string;
  eventEndDate: string;
  reporterUsername: string;
  reportTitle: string;
  status: string;
  eventPointsAwarded: number;
  createdAt: string;
  finalizedAt?: string;
  attendances: any[];
}

const AfterActionReports: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingEvents, setPendingEvents] = useState<PendingEvent[]>([]);
  const [completedAARs, setCompletedAARs] = useState<AfterActionReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'pending' | 'history'>('pending');
  const [filterYear, setFilterYear] = useState<string>('');
  const [filterMonth, setFilterMonth] = useState<string>('');

  useEffect(() => {
    fetchData();
  }, [location]); // Refetch when navigating back to this page

  const fetchData = async (year?: string, month?: string) => {
    setLoading(true);
    try {
      const historyParams = new URLSearchParams();
      if (year) historyParams.append('year', year);
      if (month) historyParams.append('month', month);
      
      const [pendingRes, historyRes] = await Promise.all([
        axios.get('/afteractionreports/pending'),
        axios.get(`/afteractionreports/history?${historyParams.toString()}`)
      ]);
      console.log('Pending events:', pendingRes.data);
      console.log('Completed AARs:', historyRes.data);
      setPendingEvents(pendingRes.data);
      setCompletedAARs(historyRes.data);
    } catch (error: any) {
      console.error('Error fetching AAR data:', error);
      if (error.response?.status === 403) {
        alert('You do not have permission to access After Action Reports');
        navigate('/home');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (year: string, month: string) => {
    setFilterYear(year);
    setFilterMonth(month);
    fetchData(year, month);
  };

  const handleCreateAAR = (eventId: number) => {
    navigate(`/after-action-reports/create/${eventId}`);
  };

  const handleEditAAR = (aarId: number) => {
    navigate(`/after-action-reports/edit/${aarId}`);
  };

  const handleViewAAR = (aarId: number) => {
    navigate(`/after-action-reports/view/${aarId}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Loading After Action Reports...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <FileText className={styles.headerIcon} size={36} />
          <div>
            <h1 className={styles.title}>After Action Reports</h1>
            <p className={styles.subtitle}>
              Document event outcomes and award participation points
            </p>
          </div>
        </div>
      </div>

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'pending' ? styles.active : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          <Clock size={20} />
          Pending Events ({pendingEvents.filter(e => !e.hasAAR).length})
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'history' ? styles.active : ''}`}
          onClick={() => setActiveTab('history')}
        >
          <CheckCircle size={20} />
          Completed Reports ({completedAARs.length})
        </button>
      </div>

      {activeTab === 'pending' && (
        <div className={styles.content}>
          {pendingEvents.length === 0 ? (
            <div className={styles.emptyState}>
              <Calendar size={48} />
              <h3>No Events Awaiting Reports</h3>
              <p>Events will appear here once they have ended.</p>
            </div>
          ) : (
            <div className={styles.grid}>
              {pendingEvents.map(event => (
                <div key={event.eventId} className={styles.card}>
                  <div className={styles.cardHeader}>
                    <h3 className={styles.cardTitle}>{event.title}</h3>
                    {event.hasAAR && (
                      <span className={styles.badge}>Draft Exists</span>
                    )}
                  </div>
                  
                  <div className={styles.cardBody}>
                    <div className={styles.cardInfo}>
                      <Calendar size={16} />
                      <span>{formatDate(event.startDate)}</span>
                    </div>
                    <div className={styles.cardInfo}>
                      <Users size={16} />
                      <span>{event.rsvpCount} RSVPs</span>
                    </div>
                  </div>

                  <div className={styles.cardFooter}>
                    {event.hasAAR && event.aarId ? (
                      <button
                        className={styles.btnSecondary}
                        onClick={() => handleEditAAR(event.aarId!)}
                      >
                        Edit Draft
                      </button>
                    ) : (
                      <button
                        className={styles.btnPrimary}
                        onClick={() => handleCreateAAR(event.eventId)}
                      >
                        Create Report
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className={styles.content}>
          <div className={styles.filterBar}>
            <select 
              className={styles.filterSelect}
              value={filterYear}
              onChange={(e) => handleFilterChange(e.target.value, filterMonth)}
            >
              <option value="">All Years</option>
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <select 
              className={styles.filterSelect}
              value={filterMonth}
              onChange={(e) => handleFilterChange(filterYear, e.target.value)}
              disabled={!filterYear}
            >
              <option value="">All Months</option>
              {['January', 'February', 'March', 'April', 'May', 'June', 
                'July', 'August', 'September', 'October', 'November', 'December']
                .map((month, i) => (
                  <option key={i + 1} value={i + 1}>{month}</option>
                ))}
            </select>
            {(filterYear || filterMonth) && (
              <button 
                className={styles.btnSecondary}
                onClick={() => handleFilterChange('', '')}
              >
                Clear Filters
              </button>
            )}
          </div>
          
          {completedAARs.length === 0 ? (
            <div className={styles.emptyState}>
              <FileText size={48} />
              <h3>No Completed Reports</h3>
              <p>Finalized reports will appear here.</p>
            </div>
          ) : (
            <div className={styles.list}>
              {completedAARs.map(aar => (
                <div key={aar.id} className={styles.listItem}>
                  <div className={styles.listItemContent}>
                    <h3 className={styles.listItemTitle}>{aar.eventTitle}</h3>
                    <p className={styles.listItemSubtitle}>{aar.reportTitle}</p>
                    <div className={styles.listItemMeta}>
                      <span>By {aar.reporterUsername}</span>
                      <span>•</span>
                      <span>{aar.attendances.filter(a => a.actuallyAttended).length} attendees</span>
                      <span>•</span>
                      <span>{aar.eventPointsAwarded} pts each</span>
                      <span>•</span>
                      <span>{formatDate(aar.finalizedAt || aar.createdAt)}</span>
                    </div>
                  </div>
                  <button
                    className={styles.btnView}
                    onClick={() => handleViewAAR(aar.id)}
                  >
                    View Report
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AfterActionReports;
